/**
 * Blood-test page — GA4 booking-funnel tracking.
 *
 * Sends three events to the existing Google tag (G-NZJTDPEGL4, loaded by the Fueled app):
 *   blood_test_cta_click  { cta_location }  any of the 5 Schedule buttons
 *   blood_test_popup_open                   the meeting popup gains [open]
 *   blood_test_form_submit                  HubSpot reports a successful submission
 *
 * Observe-only: it never calls preventDefault, so meeting-form-links.js keeps owning
 * the popup. Add ?ga_debug=1 to the URL to route events into GA4 DebugView.
 *
 * Contract:
 *   <dialog data-bt-modal data-meeting-url="<url>">   the meeting popup
 */

const GA4_ID = 'G-NZJTDPEGL4';
const MEETING_PATH = 'go.10xhealthsystem.com/meetings/10xh/blood-dx';
const FORM_ID = '51255bf7-8448-4cd9-a15a-e83d2cd20140';

// Order matters: the split/offer/closing buttons also carry the hero class.
const LOCATIONS = [
  ['blood-test-header__cta', 'header'],
  ['blood-test-split__cta', 'split'],
  ['blood-test-offer__cta', 'offer'],
  ['blood-test-cta__button', 'closing'],
  ['blood-test-hero__cta', 'hero'],
];

const ctaLocation = (link) => {
  const match = LOCATIONS.find(([className]) => link.classList.contains(className));
  return match ? match[1] : 'other';
};

/** Standard gtag stub — queues events if the Google tag hasn't finished loading yet. */
function gtagFn() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

export default function initBloodTestGa4() {
  const dialog = document.querySelector('[data-bt-modal][data-meeting-url]');
  if (!dialog) return { destroy() {} };

  const gtag = gtagFn();
  const debug = /[?&]ga_debug=1/.test(window.location.search);

  const track = (name, params) => {
    gtag('event', name, { send_to: GA4_ID, ...params, ...(debug ? { debug_mode: true } : {}) });
  };

  // 1. CTA click — any link to the booking URL outside the popup. Capture phase so the
  //    event is seen even if another handler stops propagation.
  const onClick = (e) => {
    const link = e.target.closest?.(`a[href*="${MEETING_PATH}"]`);
    if (!link || dialog.contains(link)) return;
    track('blood_test_cta_click', { cta_location: ctaLocation(link) });
  };
  document.addEventListener('click', onClick, true);

  // 2. Popup open — fires on each transition from closed to open.
  let wasOpen = dialog.open;
  const observer = new MutationObserver(() => {
    if (dialog.open && !wasOpen) track('blood_test_popup_open');
    wasOpen = dialog.open;
  });
  observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });

  // 3. Form submitted — HubSpot's documented success event, this form only.
  //    https://developers.hubspot.com/docs/api-reference/latest/marketing/forms/global-form-events
  const onSubmit = (e) => {
    if (e.detail?.formId === FORM_ID) track('blood_test_form_submit');
  };
  window.addEventListener('hs-form-event:on-submission:success', onSubmit);

  return {
    destroy() {
      document.removeEventListener('click', onClick, true);
      observer.disconnect();
      window.removeEventListener('hs-form-event:on-submission:success', onSubmit);
    },
  };
}
